/**
 * Helpers to convert planned actions into parent by object actions (for the client and server)
 */

import _, {mapObject} from 'underscore';

import {
  ObjectAction,
  ObjectActionType,
  ServerObjectAction,
  ServerParentObjectAction,
  ParentActionsByObjectId,
  ParentObjectAction,
} from 'config-migration/types/objectActions';

import {DestinationObject, MigrationObjectPlanById} from '../../MigrationConstants';
import {MigrationStep} from '../../types/MigrationStep';
import {filterMigrationObjectsToAllPlannedSteps} from '../ObjectSectionConfig';

/**
 * Given the mapping of all current objects and their plans, the list of equivalent/kept objects, and the
 *  current migration step, returns an updated mapping of the planning actions available for each object
 *
 * @param migrationPlansById The map of currently planned objects and actions (keyed by provenance id)
 * @param keptObjects Objects that exist on both the destination and the source, unchanged
 *  (equivalent provenance-id matched objects)
 * @param currentMigrationStep The current migration step. All objects in steps up to this step will be
 *  transformed.
 */
export function transformPlannedActions(
  migrationPlansById: MigrationObjectPlanById,
  keptObjects: Array<DestinationObject>,
  currentMigrationStep?: MigrationStep
): ParentActionsByObjectId {
  let plannedObjects = Object.values(migrationPlansById);
  if (currentMigrationStep) {
    plannedObjects = filterMigrationObjectsToAllPlannedSteps(plannedObjects, currentMigrationStep);
  }

  const plannedObjectsByObjectId = _.indexBy(plannedObjects, 'objectId');

  return {
    ..._.mapObject(plannedObjectsByObjectId, (migrationObjectPlan) => {
      const childPlansByObjectId = _.indexBy(migrationObjectPlan.childMigrationPlans, 'objectId');
      return {
        ...migrationObjectPlan.selectedAction,
        childActionsByObjectId: _.mapObject(childPlansByObjectId, (childPlan) => childPlan.selectedAction),
      };
    }),
    ...transformPlannedKeepActions(keptObjects, currentMigrationStep),
  };
}

/**
 * Given the current list of kept/equivalent objects, maps them from their provenance id to a `KeepAction`.
 *
 * @param keptObjects Objects that exist on both the destination and the source, unchanged
 *  (equivalent provenance-id matched objects)
 * @param currentMigrationStep The current migration step. All objects in steps up to this step will be
 *  transformed.
 *
 * Exported for testing.
 */
export function transformPlannedKeepActions(
  keptObjects: Array<DestinationObject>,
  currentMigrationStep?: MigrationStep
): ParentActionsByObjectId {
  const filteredKeptObjects = currentMigrationStep
    ? filterMigrationObjectsToAllPlannedSteps(keptObjects, currentMigrationStep)
    : keptObjects;

  const actionsByProvenanceUri: ParentActionsByObjectId = {};

  filteredKeptObjects.forEach((keptObject) => {
    actionsByProvenanceUri[keptObject.identifier.provenanceUri] = {
      type: ObjectActionType.KEEP,
      target: keptObject.identifier,
    };
  });
  return actionsByProvenanceUri;
}

function _objectActionToServer(objectAction: ObjectAction): ServerObjectAction {
  return {
    type: objectAction.type,
    target:
      objectAction.type === ObjectActionType.UPDATE || objectAction.type === ObjectActionType.KEEP
        ? {id: objectAction.target.apiIdentifier}
        : null,
  };
}

export function transformObjectActionToServerRepresentation(
  objectAction: ParentObjectAction
): ServerParentObjectAction {
  return {
    ..._objectActionToServer(objectAction),
    childActionsByObjectId: mapObject(objectAction.childActionsByObjectId || {}, _objectActionToServer),
  };
}
